import React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate, useLocation } from "react-router-dom";

type SidebarProps = {
  children?: React.ReactNode;
};

const myAccountItems = [
  { display: "Personal information", path: "personal-info" },
  { display: "My Addresses", path: "my-address" },
  { display: "My Order", path: "my-orders" },
];

const settingItems = [
  { display: "User Setting", path: "user-setting" },
  { display: "Privacy Policy", path: "privacy" },
  { display: "Cookie Policy", path: "cookie-policy" },
];

const allItems = [...myAccountItems, ...settingItems];

const Sidebar: React.FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    if (!path || path === "") return false;
    return location.pathname.startsWith(`/${path}`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Mobile: horizontal scroll tabs */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          marginTop: "3em",
        }}
      >
        {allItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Box
              key={item.path}
              onClick={() => navigate(`/${item.path}`)}
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom: active ? "2px solid #1A1A1A" : "2px solid transparent",
                transition: "border-color 0.15s ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: active ? 600 : 400,
                  color: active ? "#1A1A1A" : "#888",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease",
                }}
              >
                {item.display}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Desktop: vertical sidebar (original) */}
      <Box sx={{ display: { xs: "none", md: "block" }, maxWidth: 280, bgcolor: "background.paper", mt: 4 }}>
        <List component="nav">
          <ListItem sx={{ pb: 0 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PersonOutlineIcon sx={{ color: "#333", fontSize: 28 }} />
            </ListItemIcon>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
              My Account
            </Typography>
          </ListItem>
          <List component="div" disablePadding sx={{ pl: 5 }}>
            {myAccountItems.map((text) => (
              <ListItemButton
                key={text.display}
                onClick={() => navigate(`/${text.path}`)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={text.display}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.9rem",
                        fontWeight: isActive(text.path) ? 800 : 400,
                        color: isActive(text.path) ? "#000" : "#666",
                      },
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ my: 2 }} />
          <ListItem sx={{ pb: 0 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsOutlinedIcon sx={{ color: "#333", fontSize: 28 }} />
            </ListItemIcon>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
              Setting
            </Typography>
          </ListItem>
          <List component="div" disablePadding sx={{ pl: 5 }}>
            {settingItems.map((text) => (
              <ListItemButton
                key={text.display}
                onClick={() => navigate(`/${text.path}`)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={text.display}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.9rem",
                        fontWeight: isActive(text.path) ? 800 : 400,
                        color: isActive(text.path) ? "#000" : "#666",
                      },
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;
